/**
 * Finds the line/column range of an element's JSON within a stringified page or
 * site JSON (produced by `JSON.stringify(data, null, 4)`).
 *
 * The idPath alternates `(containerKey, indexOrId)` pairs. Whether the container
 * is a JSON array or object is decided at runtime by inspecting the bracket
 * following the key: `[` means the next segment is an array index, `{` means
 * it's an element id (used for children of groups, which are stored as an
 * id-keyed map rather than an array).
 *
 * For the terminal segment:
 *  - Array case: range spans the element's `{` to its closing `}`.
 *  - Object case: range spans the key's opening `"` to the value's closing `}`,
 *    so the highlight includes the `"<id>":` label.
 *
 * @param {string[]} idPath - e.g. ["blockId", "elements", "2"] for a top-level
 *   element, or ["blockId", "elements", "2", "elements", "CCatr8F6_"] for an
 *   element nested inside a group.
 * @param {string} jsonString - The full page JSON string (4-space indented).
 * @returns {{ startLineNumber: number, startColumn: number, endLineNumber: number, endColumn: number }}
 *   1-based range compatible with Monaco Editor's Selection API.
 */
export function findElementRange(idPath, jsonString) {
  if (!Array.isArray(idPath) || idPath.length < 3 || (idPath.length - 1) % 2 !== 0) {
    throw new Error(`Invalid idPath: ${JSON.stringify(idPath)}`);
  }

  const blockId = idPath[0];

  // Anchor to `blockData → <blockId>` first. Both page and site JSON wrap
  // element data this way (BlockContainer shape), so anchoring here avoids
  // collisions with stray occurrences of the id elsewhere (e.g. canvasSet refs).
  const rootOpen = jsonString.indexOf("{");
  if (rootOpen < 0) throw new Error("Root object not found in JSON");

  const blockData = findChildByKey(jsonString, rootOpen, "blockData");
  const block = findChildByKey(jsonString, blockData.valueStart, blockId);

  let container = block.valueStart;

  for (let p = 1; p < idPath.length; p += 2) {
    const key = String(idPath[p]);
    const segment = String(idPath[p + 1]);
    const isLast = p + 2 >= idPath.length;

    const keyStart = findKeyAtDepth1(jsonString, container, key);
    if (keyStart < 0) throw new Error(`"${key}" not found in container for "${blockId}"`);

    const valueStart = findValueOpener(jsonString, keyStart + key.length + 2);
    if (valueStart < 0) throw new Error(`Value bracket for "${key}" not found`);

    if (jsonString[valueStart] === "[") {
      const idx = parseInt(segment, 10);
      if (Number.isNaN(idx)) {
        throw new Error(`Expected numeric index for array "${key}", got "${segment}"`);
      }
      const elem = findNthObjectInArray(jsonString, valueStart, idx);
      if (isLast) return toMonacoRange(jsonString, elem.start, elem.end);
      container = elem.start;
    } else {
      const child = findChildByKey(jsonString, valueStart, segment);
      if (isLast) return toMonacoRange(jsonString, child.keyStart, child.valueEnd);
      container = child.valueStart;
    }
  }

  throw new Error(`Unreachable: idPath ${JSON.stringify(idPath)}`);
}

// Returns the offset of the closing `"` of a JSON string starting at `start`.
// Respects `\"` escapes. JSON.stringify never embeds literal newlines, so an
// unterminated string is treated as a hard error.
function skipString(s, start) {
  let i = start + 1;
  while (i < s.length) {
    if (s[i] === "\\") {
      i += 2;
      continue;
    }
    if (s[i] === '"') return i;
    i++;
  }
  throw new Error("Unterminated string in JSON");
}

// Finds the first `{` or `[` at or after `start`. Returns -1 if none.
function findValueOpener(s, start) {
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (ch === "{" || ch === "[") return i;
  }
  return -1;
}

// Given an offset pointing at an object's opening `{`, finds the offset of
// the opening `"` of `key` among that object's direct properties. Returns -1
// if not found. Works by walking with a depth counter and matching keys only
// when depth === 1 (i.e. one level inside the container).
function findKeyAtDepth1(s, containerOffset, key) {
  const needle = `"${key}"`;
  let depth = 0;
  let i = containerOffset;

  while (i < s.length) {
    const ch = s[i];
    if (ch === '"') {
      if (depth === 1 && s.startsWith(needle, i)) {
        let k = i + needle.length;
        while (k < s.length && (s[k] === " " || s[k] === "\t")) k++;
        if (s[k] === ":") return i;
      }
      i = skipString(s, i) + 1;
      continue;
    }
    if (ch === "{" || ch === "[") depth++;
    else if (ch === "}" || ch === "]") {
      depth--;
      if (depth === 0) return -1;
    }
    i++;
  }
  return -1;
}

// Given an offset pointing at an opening `{` or `[`, returns the offset of
// the matching close. Throws on mismatch or truncation.
function findMatchingClose(s, openOffset) {
  const open = s[openOffset];
  const close = open === "{" ? "}" : "]";
  let depth = 0;

  for (let i = openOffset; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      i = skipString(s, i);
      continue;
    }
    if (ch === "{" || ch === "[") depth++;
    else if (ch === "}" || ch === "]") {
      depth--;
      if (depth === 0) {
        if (ch !== close) throw new Error(`Mismatched closing bracket (expected ${close})`);
        return i;
      }
    }
  }
  throw new Error("Unterminated container in JSON");
}

// Given an offset pointing at an object's opening `{`, finds the child entry
// for `targetKey`. Returns the key's opening `"`, the value's opening bracket,
// and the value's closing bracket.
function findChildByKey(s, objectOffset, targetKey) {
  const keyStart = findKeyAtDepth1(s, objectOffset, targetKey);
  if (keyStart < 0) throw new Error(`Key "${targetKey}" not found`);

  const valueStart = findValueOpener(s, keyStart + targetKey.length + 2);
  if (valueStart < 0) throw new Error(`Value for key "${targetKey}" not found`);

  const valueEnd = findMatchingClose(s, valueStart);
  return { keyStart, valueStart, valueEnd };
}

// Given an offset pointing at an array's opening `[`, returns the `{` and `}`
// offsets of the Nth object element (0-indexed) at depth 0 inside the array.
function findNthObjectInArray(s, arrayOffset, targetIndex) {
  let depth = 0;
  let currentIndex = -1;
  let objStart = -1;

  for (let i = arrayOffset + 1; i < s.length; i++) {
    const ch = s[i];
    if (ch === '"') {
      i = skipString(s, i);
      continue;
    }
    if (ch === "]" && depth === 0) {
      throw new Error(`Array index ${targetIndex} out of bounds`);
    }
    if (ch === "{") {
      if (depth === 0) {
        currentIndex++;
        if (currentIndex === targetIndex) objStart = i;
      }
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0 && currentIndex === targetIndex) {
        return { start: objStart, end: i };
      }
    }
  }

  throw new Error(`Array index ${targetIndex} not found`);
}

function toMonacoRange(s, startOffset, endOffset) {
  const start = offsetToPosition(s, startOffset);
  const end = offsetToPosition(s, endOffset);
  return {
    startLineNumber: start.line + 1,
    startColumn: start.col + 1,
    endLineNumber: end.line + 1,
    endColumn: end.col + 2,
  };
}

function offsetToPosition(s, offset) {
  let line = 0;
  let lineStart = 0;
  for (let i = 0; i < offset; i++) {
    if (s[i] === "\n") {
      line++;
      lineStart = i + 1;
    }
  }
  return { line, col: offset - lineStart };
}
